import {
  Box,
  AppBar as MuiAppBar,
  Toolbar as MuiToolbar,
  styled,
  TabProps,
} from '@mui/material';
import { type JSX } from 'react';
import {
  Link, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { IPatient } from '@zero-dash/types';
import { MAINTENANCE_BAR_HEIGHT_CSS_VAR } from '@/constants/layout';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';
import { useIsUserAuthorised } from '@/hooks/useIsUserAuthorised';
import CustomTypography from '../../Common/Typography';
import ZeroLogoWithRec from '../../CustomIcons/ZeroLogoWithRec';
import { Notifications } from '../../Notifications/Notifications';
import Gender from '../../VitalStatus/Gender';
import { CustomTabs } from '../../Common/Tabs';

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  height: '80px',
  top: `var(${MAINTENANCE_BAR_HEIGHT_CSS_VAR})`,
  backgroundColor: theme.colours.core.white,
  color: theme.colours.core.offBlack100,
  boxShadow: 'none',
  zIndex: 0,
  paddingRight: '48px',
  borderBottom: `1px solid ${theme.colours.core.grey50}`,
}));

const Toolbar = styled(MuiToolbar)({
  height: '100%',
  padding: 0,
});

const Header = styled(Box)({
  display: 'flex',
  marginRight: 'auto',
  paddingLeft: 21,
  gap: '50px',
});

const LogoLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  lineHeight: 0,
  textDecoration: 'none',
});

type IProps = {
  patientRecords: IPatient[];
};

const tabs: TabProps[] = [
  {
    label: 'Timeline',
    value: 'timeline',
  },
  {
    label: 'Curation',
    value: 'curation',
  },
];

export default function TimelineNavBar({ patientRecords }: IProps): JSX.Element {
  const navigate = useNavigate();
  const { patientId, publicSubjectId } = useParams();
  const location = useLocation();
  const zeroDashSdk = useZeroDashSdk();
  const canViewCuration = useIsUserAuthorised('curation.sample.read');

  const visibleTabs = tabs.filter((tab) => tab.value !== 'curation' || canViewCuration);
  const activeTab = location.pathname.includes('/curation') ? 'curation' : 'timeline';
  const patientIds = [...new Set(patientRecords.map((data) => data.patientId))].join(', ');
  const vitalStatus = (patientRecords.length && patientRecords[0].vitalStatus) || 'Unknown';
  const sex = (patientRecords.length > 0 && patientRecords[0].sex) || 'Unknown';

  const handleTabChange = async (value: string): Promise<void> => {
    if (value === activeTab || !publicSubjectId) return;
    const sets = await zeroDashSdk.curation.analysisSets.getAnalysisSets({ publicSubjectId });
    const id = sets[0]?.analysisSetId;
    if (id) {
      navigate(`/${publicSubjectId}/${patientId}/${id}/curation`);
    }
  };

  return (
    <AppBar>
      <Toolbar disableGutters>
        <LogoLink to="/">
          <ZeroLogoWithRec />
        </LogoLink>
        <Header>
          <Box display="flex" alignItems="center" gap="10px">
            <CustomTypography variant="titleRegular" fontWeight="medium">
              Patient ID(s):
              &nbsp;
              {patientIds}
            </CustomTypography>
            <Gender vitalStatus={vitalStatus} gender={sex} />
          </Box>
          <CustomTabs
            variant="navigation"
            tabs={visibleTabs}
            value={activeTab}
            onChange={(e, v) => { handleTabChange(v); }}
            size="xlarge"
          />
        </Header>
        <Notifications />
      </Toolbar>
    </AppBar>
  );
}
